/**
 * client/src/lib/step-up-error-copy.ts — error-code -> user copy for the AAL
 * step-up ceremony (extracted from use-step-up-aal3.ts, 300-line modularity
 * split).
 *
 * Amber-tier error mapping — CLAUDE.md §Design Constraints (Colour) /
 * DESIGN-SYSTEM.md: AAL-gate copy is amber (never red — red is reserved for
 * Forensic Pause + critical OSINT only). Extend this table if the server adds
 * a new WebAuthnError code to the step-up ceremony; remove a row if a code
 * retires.
 *
 * `genericFallback` lets begin/complete give a stage-specific generic message
 * ("Couldn't start verification" vs "Verification failed") while sharing the
 * cancel / NO_HARDWARE_KEY / WRONG_AUTHENTICATOR_TYPE / DUAL_* / network
 * mappings, which are identical regardless of which leg of the ceremony failed.
 *
 * Copy is context-free: this hook backs SIX call sites (add-bank ×2, co-sign,
 * reveal-authorize, revealed-account, org-vault reveal) and the strings render
 * verbatim at each — so no message may name a specific action (e.g. "guardian
 * requests") or a UI control that doesn't exist here. Plain language only —
 * never "AAL3", "UV", or "credential"; say passkey / Level 3, fresh
 * (Friction Rule clause 2 — these are user-facing, swept surfaces).
 *
 * Error-code source: server/logic/webauthn/step-up/begin.ts (NO_CREDENTIALS,
 * NO_HARDWARE_KEY, DUAL_NOT_AVAILABLE),
 * server/logic/webauthn/step-up-dual{,-eligibility}.ts
 * (DUAL_LEG_IMMATURE, DUAL_DISTINCT_REQUIRED, DUAL_WINDOW_EXPIRED,
 * DUAL_INSUFFICIENT_CREDENTIALS), and server/logic/webauthn/challenge-respond.ts
 * (WRONG_AUTHENTICATOR_TYPE, thrown by the shared verifyFreshUvAssertion
 * primitive when requireCrossPlatform=true and the credential's FIDO MDS
 * attestation was not verified at registration). All non-2xx responses reach
 * the client as ApiError via handleWebAuthnError -> apiRequest (400 for all
 * of the above per server/routes/webauthn-register-schemas.ts — none is in
 * that function's explicit status map, so all fall to the 400 default).
 */

import { ApiError } from "@/lib/queryClient";
import { isUserCancel } from "@/lib/payment-rail/webauthn-cancel";
import { MASTER_ERROR_MESSAGES } from "@/lib/webauthn-errors/master-messages";
import { NETWORK_PROBLEM_MESSAGE } from "@/lib/error-copy";

/**
 * Server error code carried by a thrown ceremony failure, or null when the
 * throw carries none (user cancel, network TypeError, anything non-ApiError).
 * Lives here — beside the code->copy table — so a caller that needs to BRANCH
 * on the code (e.g. rendering a structural setup destination instead of a
 * dead-end retry) reads the same source of truth the copy does, rather than
 * re-deriving it from the message string.
 */
export function stepUpErrorCode(err: unknown): string | null {
  return err instanceof ApiError && typeof err.code === "string" ? err.code : null;
}

export function mapStepUpError(err: unknown, genericFallback: string): string {
  if (isUserCancel(err)) {
    return "You cancelled the security key prompt. Tap the button again to retry.";
  }
  if (err instanceof ApiError) {
    // NO_CREDENTIALS and NO_HARDWARE_KEY are DIFFERENT states and must never
    // share copy (step-up/begin.ts — the NO_CREDENTIALS vs NO_HARDWARE_KEY branches). NO_CREDENTIALS means the account
    // holds no active authenticator at all; NO_HARDWARE_KEY means it holds
    // several, but none is attested AND fewer than two are distinct ≥24h-old
    // passkeys (the dual basis's own floor) — the account has usable keys,
    // they just don't clear either AAL3 route yet. Collapsing NO_CREDENTIALS
    // and NO_HARDWARE_KEY told a user with working keys that none was
    // registered — which reads as the enrolment not having stuck, and sent
    // them to re-register instead of toward a real route. Credentials are
    // ACCOUNT-scoped, never device-scoped: "on this device" was wrong in
    // both branches.
    if (err.code === "NO_CREDENTIALS") {
      return "No security key is registered on your account. Add one in Settings to continue.";
    }
    if (err.code === "NO_HARDWARE_KEY") {
      return "None of your keys is an attested hardware key, and you don't yet have two passkeys that have each been " +
        "on your account for 24 hours — either one unlocks this. Add an attested hardware key, or a " +
        "second passkey, in Settings to continue.";
    }
    if (err.code === "MASTER_HARDWARE_CONFLICT") {
      // Non-retryable (master-messages.ts `retryable: false`): a master passkey
      // that isn't an attested hardware key can never satisfy this check, so
      // the generic "Verification failed. Try again." this used to fall through
      // to was simply false. REUSES the shipped copy rather than writing a
      // second message for the same code (ledger #86 clause 4).
      return MASTER_ERROR_MESSAGES.MASTER_HARDWARE_CONFLICT.description;
    }
    if (err.code === "WRONG_AUTHENTICATOR_TYPE") {
      // Account-scoped, plain-language (module header rule): never "AAL3",
      // never "this device" — the key that answered simply isn't attested.
      return "That passkey isn't an attested hardware key, which this check needs. Use your attested hardware key instead.";
    }
    // Dual-passkey basis (server/logic/webauthn/step-up-dual.ts) failures.
    if (err.code === "DUAL_DISTINCT_REQUIRED") {
      return "That was the same passkey — the second check needs a different one.";
    }
    if (err.code === "DUAL_WINDOW_EXPIRED") {
      return "The two checks were too far apart. Start again and use both passkeys within a few minutes.";
    }
    if (err.code === "DUAL_LEG_IMMATURE") {
      return "A passkey can be used for this check 24 hours after it was added.";
    }
    if (err.code === "DUAL_INSUFFICIENT_CREDENTIALS") {
      return "A second distinct passkey isn't available for this check right now. Try again once you " +
        "have another one that's been on your account for 24 hours.";
    }
    if (err.code === "DUAL_NOT_AVAILABLE") {
      // Explicitly-requested dual basis (ledger #86) refused at begin: the
      // account doesn't currently hold two distinct ≥24h-old passkeys.
      return "The two-passkey check isn't available right now — it needs two passkeys that have " +
        "each been on your account for 24 hours.";
    }
    return genericFallback;
  }
  if (err instanceof TypeError) {
    // fetch() throws a bare TypeError ("Failed to fetch") on network failure —
    // it never reaches apiRequest's ApiError wrapping because there is no response.
    return NETWORK_PROBLEM_MESSAGE;
  }
  return genericFallback;
}
